/* Uit Krabbelpaal.dc.html; bodies letterlijk overgenomen. */

import { AV, AV_EMPTY, BIO_MAX, GH, GW } from '../lib/config.js';
import { playParty } from '../lib/sound.js';
import { parseTs } from '../lib/text.js';
import * as store from '../lib/store.js';

export default {
  /**
   * Het welkomstscherm dicht.
   *
   * Zonder vlag in de opslag: dit scherm komt bij elke start terug, dus er valt
   * niets te onthouden. Wat er wel was — gp_welcome_v2 — bepaalde vroeger of je
   * het welkom of het nieuwsvenster kreeg. Allebei die vensters zijn weg.
   */
  closeWelcome() { playParty(); this.setState({ welcome: false }); },

  /**
   * De poort voor alles wat een naam nodig heeft.
   *
   * Elke handeling die iets van jou op de server zet vraagt hier eerst om: kletsen,
   * hartjes geven, reageren, stemmen, krabbelen op het gastenboek, een ideetje
   * insturen. Heb je geen naam, dan gaat het accountscherm open met de reden erin.
   *
   * Eén poort, want anders krijgt de helft van de app een eigen foutregeltje onder
   * een veld en de andere helft een venster. Zie openAccount hierboven voor waar je
   * dan landt: nieuwe mensen in de velden, wie hier eerder was in de lijst.
   */
  ensureName(msg) {
      // Een naam én een account. Er kan in een oude browser nog een losse naam
      // staan uit de tijd voor de accounts; alles wat je nu instuurt draagt een
      // accountId, dus met alleen een naam zou je iets achterlaten dat later van
      // niemand blijkt te zijn.
      if (this.state.author && this.state.accountId) return this.state.author;
      this.openAccount(msg || 'Log in of maak een account aan om mee te doen.');
      return '';
    },

  clearLikeBadge() {
      const mine = this.state.galleryItems.reduce((sum, g) => this.isMine(g) ? sum + g.likesCount : sum, 0);
      store.set('gp_likes_seen', String(mine));
      this.setState({ likesSeen: mine });
    },

  /**
   * De invulvelden van je eigen account, gevuld met wat er nu in staat.
   *
   * Apart gezet omdat er twee wegen naar de invulvelden lopen en die tweede ze niet
   * vulde. Zie openAccountEditor in logic/render-vals.js: die zette alleen de vlag
   * `accEdit` om. Kwam je via iemands profiel in de lijst terecht — dat gaat via
   * openProfile, en die roept openAccount niet aan — dan stonden de velden nog leeg
   * en kreeg je een leeg formulier voor je neus dat eruitzag als "account aanmaken".
   * Op opslaan drukken had daar je eigen naam gewist.
   */
  accountEditFields() {
      const mij = this.state.accounts.find((a) => a.id === this.state.accountId) || {};
      return {
        editName: this.state.author,
        editBio: mij.bio || '',
        editFavFood: mij.favFood || '',
        editAvatar: this.state.avatar && this.state.avatar.length === AV * AV
          ? this.state.avatar
          : ((() => { try { const d = store.get('gp_avatar_draft'); return d && d.length === AV * AV ? d : AV_EMPTY; } catch (e) { return AV_EMPTY; } })())
      };
    },

  /** De invulvelden openen met je eigen gegevens erin. */
  openAccountEditor() {
      return this.setState(Object.assign({ accEdit: true, accountError: '', profileDirty: false }, this.accountEditFields()));
    },

  /**
   * Het scherm met alle plekjes aan de paal, zonder profiel en zonder velden.
   *
   * Deze drie regels stonden op drie plekken los: in de knop rechtsboven, in het
   * postvakje en in het startmenu. openAccount zet accEdit standaard aan voor wie
   * nog geen account heeft, en dat is hier nooit de bedoeling: je komt kijken wie
   * er allemaal zijn.
   */
  openIedereen() {
      this.openAccount();
      // De uitklapper in de hoek regelt openAccount zelf; hier blijft alleen staan
      // dat het om de lijst gaat en niet om iemands profiel.
      this.setState({ accEdit: false, profileId: null });
    },

  /**
   * Het accountscherm.
   *
   * De naamlijst is een uitklapper in de rechterbovenhoek, onder de knop met de
   * kopjes waar je hem mee opendoet. Hij stond ook als venster van 640 in het
   * midden van de paal, met een donkere laag erachter, en dat is er nu uit: een
   * lijst waar je één naam uit kiest hoeft de hele paal niet af te dekken. Het
   * invulformulier en een profiel staan wél in het midden, want die zijn breder
   * dan 336 en je bent er even bezig. Zie accModalStyle in logic/render-vals.js;
   * die zet de hoek weer uit zodra accEdit of profileId aan staat.
   */
  openAccount(msg, profileId) { return this.setState(Object.assign({
      dialog: 'account',
      notifOpen: false,
      themePopup: false,
      themeHidden: false,
      themeWords: {},
      themeEdits: {},
      themeError: '',
      slot: 0,
      slotArt: ['', ''],
      projError: '',
      profileId: profileId || null,
      avatarEditorOpen: false,
      accountHint: typeof msg === 'string' ? msg : '',
      // Zonder account kom je op de lijst met namen: "Wie ben jij?". Nooit meteen
      // in de invulvelden, ook niet als je hier nieuw bent en ook niet als er een
      // reden meegekomen is. Er staan vierentwintig collega's op die lijst en bijna
      // iedereen die hier binnenkomt staat er al bij; een leeg formulier
      // "Account aanmaken" voor hun neus is dan de uitnodiging om zichzelf voor de
      // tweede keer aan te maken. Wie er echt nog niet bij staat klikt op de roze
      // knop erboven.
      //
      // Ben je al ingelogd, dan zegt een reden dat er iets aan je eigen account
      // moet gebeuren en gaan de velden wel open; zie onPublish in logic/publish.js.
      accEdit: !!this.state.accountId && !!msg,
      accountError: '',
      editPin: '',
      savingAccount: false,
      avPainting: false,
      profileDirty: false
    }, this.accountEditFields()), () => { this.fetchAccounts(); this.clearLikeBadge(); }); },

  onNameInput(e) { return this.setState({ editName: e.target.value, profileDirty: true }); },

  onBioInput(e) { return this.setState({ editBio: e.target.value.slice(0, BIO_MAX), profileDirty: true }); },

  onFavFoodInput(e) { return this.setState({ editFavFood: e.target.value.slice(0, BIO_MAX), profileDirty: true }); },

  /**
   * De avatar-tekenaar heeft twee uitgangen, en die doen niet hetzelfde.
   *
   * "Klaar" houdt wat je getekend hebt. Het kruisje, een tik ernaast en Escape gooien
   * het weg. Daarom wordt hier bewaard hoe je avatar erbij stond toen je begon: dat
   * is waar het kruisje je naar terugbrengt.
   *
   * profileDirty gaat mee in die momentopname. Anders staat het accountscherm na een
   * weggegooide sessie te denken dat er nog iets te bewaren valt.
   */
  openAvatarEditor() {
      return this.setState({
        avatarEditorOpen: true, avUndo: [], avRedo: [], avErase: false,
        avTool: 'pen', avSel: null, avPreview: null,
        avStart: this.state.editAvatar || AV_EMPTY,
        avStartDirty: !!this.state.profileDirty
      });
    },

  /** Klaar: houden wat er staat. */
  closeAvatarEditor() { return this.setState({ avatarEditorOpen: false, avPainting: false, avErase: false, avSel: null, avPreview: null }); },

  /** Kruisje, ernaast tikken of Escape: terug naar hoe het was toen je begon. */
  cancelAvatarEditor() {
      return this.setState({
        avatarEditorOpen: false, avPainting: false, avErase: false,
        avUndo: [], avRedo: [], avSel: null, avPreview: null,
        editAvatar: this.state.avStart || AV_EMPTY,
        profileDirty: !!this.state.avStartDirty
      }, this.keepAvatarDraft);
    },

  // Ook vanuit het leaderbord: schakel altijd naar het accountvenster.
  // Begroeting per dagdeel, ook de titel van het venster.
  greetingLine() {
      const hr = new Date().getHours();
      const me = this.state.accounts.find((a) => a.id === this.state.accountId);
      const first = ((me && me.name) || this.state.author || '').split(' ')[0];
      const dagdeel = hr < 6 ? 'Goedenacht' : hr < 12 ? 'Goedemorgen' : hr < 18 ? 'Goedemiddag' : 'Goedenavond';
      return first ? dagdeel + ', ' + first : dagdeel + '!';
    },

  // Regels voor de ticker onderin.
  hallVals(onlineUsers, openForMe, streakOfKey) {
      const streak = this.state.accountId ? streakOfKey('id:' + this.state.accountId) : 0;
      const online = (onlineUsers || []).filter((o) => o.id !== this.state.accountId);
      const items = this.state.galleryItems || [];
      const nl = (n) => n.toLocaleString('nl-NL');
      const lines = [];
      if (online.length) lines.push({ icon: '🟢', text: online.length === 1 ? online[0].name + ' is er ook' : online.length + ' krabbelaars zijn online' });
      const theme = this.themeOfDay();
      if (theme) lines.push({ icon: '🎨', text: 'Thema van vandaag: ' + theme });
      if ((openForMe || []).length) lines.push({ icon: '✉️', text: (openForMe.length === 1 ? 'Eén verzoekje' : openForMe.length + ' verzoekjes') + ' wacht' + (openForMe.length === 1 ? '' : 'en') + ' op jou' });
      if (streak >= 2) lines.push({ icon: '🔥', text: 'Je krabbelt ' + streak + ' dagen op rij. Niet nu stoppen' });
  
      // Hartjes: die van jou, en de kraker van de paal.
      const myLikes = items.reduce((s, g) => (this.isMine(g) ? s + (g.likesCount || 0) : s), 0);
      if (myLikes > 0) lines.push({ icon: '❤️', text: 'Jouw krabbels kregen samen ' + nl(myLikes) + (myLikes === 1 ? ' hartje' : ' hartjes') });
      const totalLikes = items.reduce((s, g) => s + (g.likesCount || 0), 0);
      if (totalLikes > 0) lines.push({ icon: '💘', text: nl(totalLikes) + ' hartjes uitgedeeld aan de paal' });
      const top = items.slice().sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0))[0];
      if (top && top.likesCount > 0) lines.push({ icon: '👑', text: '"' + (top.title || 'naamloos') + '" van ' + (top.author || 'iemand') + ' is de hartjeskampioen met ' + top.likesCount });
      const latest = items.slice().sort((a, b) => parseTs(b.created) - parseTs(a.created))[0];
      if (latest) lines.push({ icon: '📌', text: (latest.author || 'iemand') + ' plakte "' + (latest.title || 'naamloos') + '" op de paal' });
  
      if (items.length) lines.push({ icon: '🖼️', text: nl(items.length) + (items.length === 1 ? ' krabbel hangt' : ' krabbels hangen') + ' aan de paal' });
      if (items.length) lines.push({ icon: '🔬', text: 'Samen al ' + nl(items.length * GW * GH) + ' pixels bij elkaar geklikt' });
      const done = (this.state.reqList || []).filter((r) => r.status === 'done').length;
      if (done) lines.push({ icon: '🤝', text: nl(done) + (done === 1 ? ' verzoekje ingewilligd' : ' verzoekjes ingewilligd') });
      lines.push({ icon: '👥', text: this.state.accounts.length + ' krabbelaars hebben een plekje aan de paal' });
  
      // Onzin tussendoor, want een ticker zonder onzin is maar een tabel.
      lines.push({ icon: '🥔', text: 'Wist je dat een aardappel ook in 32 bij 32 past? Bewezen door de wetenschap' });
      lines.push({ icon: '☕', text: 'De koffie is op. De paal niet' });
      lines.push({ icon: '🐌', text: 'Deze balk beweegt sneller dan de meeste maandagen' });
      lines.push({ icon: '📎', text: 'Het lijkt erop dat je een krabbel wilt maken. Wil je hulp? Nee' });
      lines.push({ icon: '🛸', text: 'Nog geen buitenaards leven gespot aan de paal, maar we blijven kijken' });
      return { tickerItems: lines };
    },

  openProfile(id) { return this.setState({ dialog: 'account', profileId: id, accountError: '', accountHint: '', accEdit: false }); },

  closeProfile() { return this.setState({ profileId: null }); },

  askKrabbelFrom(id) { return this.setState({ dialog: 'inbox', reqTo: id, reqError: '', profileId: null, notifOpen: false }, this.fetchRequests); },

  /**
   * Eén vakje van je avatar zetten.
   *
   * `gum` maakt het vakje doorzichtig, wat de rechtermuisknop doet zonder dat je van
   * kleur hoeft te wisselen. Precies zoals het gummen op het grote doek werkt.
   */
  avPaint(i, gum) {
      const ch = (gum || this.state.avColor < 0) ? '.' : this.state.avColor.toString(16);
      const cur = this.state.editAvatar || AV_EMPTY;
      if (cur[i] === ch) return;
      this.setState({ editAvatar: cur.slice(0, i) + ch + cur.slice(i + 1), profileDirty: true }, this.keepAvatarDraft);
    },

  /**
   * De stand van vóór deze streek bewaren, zodat Cmd+Z hem terug kan halen.
   *
   * Eén stap per streek en niet per vakje: sleep je over tien vakjes, dan haalt één
   * keer ongedaan maken die tien terug. Zo doet het grote doek het ook.
   */
  avPushUndo() {
      const stapel = (this.state.avUndo || []).concat([this.state.editAvatar || AV_EMPTY]).slice(-40);
      this.setState({ avUndo: stapel, avRedo: [] });
    },

  avDown(i, e) {
      if (this.state.avTool === 'sel') { this.avSelDown(i, e); return; }
      // De rechtermuisknop gumt, en alleen zolang je hem vasthoudt.
      const gum = !!e && e.button === 2;
      this.avPushUndo();
      this.setState({ avPainting: true, avErase: gum });
      this.avPaint(i, gum);
    },

  // Met het kader in de hand doet het raster niets met een enter of een leave: dat
  // slepen hangt aan window. Zie avSelDown.
  avEnter(i) {
      if (this.state.avTool === 'sel') return;
      if (this.state.avPainting) this.avPaint(i, this.state.avErase);
    },

  avStopPaint() {
      if (this.state.avPainting) this.setState({ avPainting: false, avErase: false });
    },

  /**
   * Het kader in de avatar-tekenaar.
   *
   * Werkt zoals het kader op het grote doek (logic/selection.js), en dat is meer dan
   * cosmetiek: die twee dingen die het doek doet en ik eerst niet deed, waren precies
   * de twee bugs.
   *
   * 1. **Slepen hangt aan `window`, niet aan de vakjes.** Ik luisterde op
   *    `onMouseEnter` per vakje en stopte bij `onMouseLeave` van het raster. Daardoor
   *    stierf een sleep zodra je even buiten het raster kwam, en, erger: zonder een
   *    vlag "ik sleep nu" bleef het kader zich aanpassen terwijl je alleen maar met
   *    je muis over het raster ging, zonder ingedrukte knop.
   * 2. **De pixels worden één keer opgetild.** Ik sneed ze bij elke stap opnieuw uit,
   *    en dan vreet het gat van de vorige stap de pixels van de volgende op: sleep je
   *    over je eigen spoor, dan bleef er niets over. Nu wordt het blok bij het
   *    oppakken bewaard, het gat één keer gemaakt, en daarna alleen nog neergezet.
   *
   * De avatar is een string van vierenzestig tekens, dus dit rekent met tekens waar
   * het doek met ImageData rekent. Een lasso zit er niet in: op acht bij acht is een
   * rechthoek genoeg.
   */
  avXY(i) { return { x: i % AV, y: Math.floor(i / AV) }; },

  avKlem(n) { return Math.max(0, Math.min(AV - 1, n)); },

  /**
   * Welk vakje ligt er onder de muis? Uit de maten van het raster, niet uit een event
   * op een vakje: zo blijft slepen werken als je even buiten het raster komt.
   *
   * De rand van drie pixels hoort er niet bij. Zonder die aftrek schuift de hele
   * afbeelding een fractie op, en aan de rand van het raster wijs je dan het vakje
   * ernaast aan.
   */
  avCelOnder(e) {
      const el = this.avGridRef && this.avGridRef.current;
      if (!el) return null;
      const r = el.getBoundingClientRect();
      const rand = parseFloat(getComputedStyle(el).borderTopWidth) || 0;
      const w = r.width - 2 * rand;
      const h = r.height - 2 * rand;
      if (w <= 0 || h <= 0) return null;
      return {
        x: Math.floor(((e.clientX - r.left - rand) / w) * AV),
        y: Math.floor(((e.clientY - r.top - rand) / h) * AV)
      };
    },

  avInSel(i) {
      const s = this.state.avSel;
      if (!s) return false;
      const { x, y } = this.avXY(i);
      return x >= s.x && x < s.x + s.w && y >= s.y && y < s.y + s.h;
    },

  /** Het vlak dat je aan het trekken bent, als hoeken. Alleen tijdens die sleep. */
  avPreviewRect() {
      const p = this.state.avPreview;
      if (!p) return null;
      return {
        x: Math.min(p.x0, p.x1), y: Math.min(p.y0, p.y1),
        w: Math.abs(p.x1 - p.x0) + 1, h: Math.abs(p.y1 - p.y0) + 1
      };
    },

  avInPreview(i) {
      const r = this.avPreviewRect();
      if (!r) return false;
      const { x, y } = this.avXY(i);
      return x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;
    },

  avClearSel() {
      if (!this.state.avSel && !this.state.avPreview) return false;
      this.setState({ avSel: null, avPreview: null });
      return true;
    },

  /**
   * Muisknop omlaag met het kader in de hand.
   *
   * Binnen een vlak dat er al ligt pak je het op. Daarbuiten trek je een nieuw vlak.
   * Rechtsklikken laat los, zoals rechtsklikken bij het tekenen gumt.
   */
  avSelDown(i, e) {
      if (e && e.button === 2) { this.avClearSel(); return; }
      const cel = this.avXY(i);
      if (this.avInSel(i)) {
        const s = this.state.avSel;
        const cur = (this.state.editAvatar || AV_EMPTY).split('');
        // Eén keer optillen: het blok bewaren, en het gat maken.
        const blok = [];
        for (let y = 0; y < s.h; y++) {
          for (let x = 0; x < s.w; x++) {
            const j = (s.y + y) * AV + (s.x + x);
            blok.push(cur[j]);
            cur[j] = '.';
          }
        }
        this.avPushUndo();
        this.avBlok = { cellen: blok, w: s.w, h: s.h };
        this.avGat = cur.join('');
        this.avVan = { x: cel.x, y: cel.y, sx: s.x, sy: s.y };
        this.avZet(s.x, s.y);
      } else {
        this.avVan = null;
        this.setState({ avSel: null, avPreview: { x0: cel.x, y0: cel.y, x1: cel.x, y1: cel.y } });
      }
      window.addEventListener('mousemove', this.avWinMove);
      window.addEventListener('mouseup', this.avWinUp);
    },

  /** Het opgetilde blok op (nx, ny) neerzetten, bovenop het gat. */
  avZet(nx, ny) {
      const b = this.avBlok;
      if (!b) return;
      const cur = (this.avGat || AV_EMPTY).split('');
      for (let y = 0; y < b.h; y++) {
        for (let x = 0; x < b.w; x++) {
          const px = nx + x, py = ny + y;
          if (px < 0 || py < 0 || px >= AV || py >= AV) continue;
          cur[py * AV + px] = b.cellen[y * b.w + x];
        }
      }
      this.setState({
        editAvatar: cur.join(''),
        profileDirty: true,
        avSel: { x: nx, y: ny, w: b.w, h: b.h }
      }, this.keepAvatarDraft);
    },

  avWinMove(e) {
      const cel = this.avCelOnder(e);
      if (!cel) return;
      if (this.avVan && this.avBlok) {
        const s = this.state.avSel;
        // Buiten het raster mag: zo schuif je een stuk helemaal weg.
        const nx = Math.max(-s.w, Math.min(AV, this.avVan.sx + (cel.x - this.avVan.x)));
        const ny = Math.max(-s.h, Math.min(AV, this.avVan.sy + (cel.y - this.avVan.y)));
        if (nx === s.x && ny === s.y) return;
        this.avZet(nx, ny);
        return;
      }
      const p = this.state.avPreview;
      if (!p) return;
      const x1 = this.avKlem(cel.x), y1 = this.avKlem(cel.y);
      if (p.x1 === x1 && p.y1 === y1) return;
      this.setState({ avPreview: { x0: p.x0, y0: p.y0, x1: x1, y1: y1 } });
    },

  /** Loslaten: het vlak vastzetten, of het verplaatsen afronden. */
  avWinUp() {
      window.removeEventListener('mousemove', this.avWinMove);
      window.removeEventListener('mouseup', this.avWinUp);
      this.avBlok = null;
      this.avGat = null;
      this.avVan = null;
      const r = this.avPreviewRect();
      if (r) this.setState({ avPreview: null, avSel: r });
    },

  /** Pijltjes: één vakje, met Shift een hele vlakbreedte. */
  avNudge(dx, dy, groot) {
      const s = this.state.avSel;
      if (!s) return false;
      const stap = groot ? { x: s.w, y: s.h } : { x: 1, y: 1 };
      const cur = (this.state.editAvatar || AV_EMPTY).split('');
      const blok = [];
      for (let y = 0; y < s.h; y++) {
        for (let x = 0; x < s.w; x++) {
          const j = (s.y + y) * AV + (s.x + x);
          blok.push(cur[j]);
          cur[j] = '.';
        }
      }
      this.avPushUndo();
      this.avBlok = { cellen: blok, w: s.w, h: s.h };
      this.avGat = cur.join('');
      this.avZet(
        Math.max(-s.w, Math.min(AV, s.x + dx * stap.x)),
        Math.max(-s.h, Math.min(AV, s.y + dy * stap.y))
      );
      this.avBlok = null; this.avGat = null;
      return true;
    },

  /** Een kleur kiezen terwijl er een vlak ligt: vul dat vlak. */
  avVulSel(kleur) {
      const s = this.state.avSel;
      if (!s) return false;
      const ch = kleur < 0 ? '.' : kleur.toString(16);
      const cur = (this.state.editAvatar || AV_EMPTY).split('');
      for (let y = 0; y < s.h; y++) {
        for (let x = 0; x < s.w; x++) {
          const px = s.x + x, py = s.y + y;
          if (px < 0 || py < 0 || px >= AV || py >= AV) continue;
          cur[py * AV + px] = ch;
        }
      }
      this.avPushUndo();
      this.setState({ editAvatar: cur.join(''), profileDirty: true }, this.keepAvatarDraft);
      return true;
    },

  /** Wisselen tussen tekenen en het kader. */
  avPickTool(tool) { return this.setState({ avTool: tool, avSel: null, avPreview: null }); },

  avPickEraser() {
      if (this.avVulSel(-1)) return;
      return this.setState({ avColor: -1 });
    },

  avClear() {
      this.avPushUndo();
      return this.setState({ editAvatar: AV_EMPTY, profileDirty: true }, this.keepAvatarDraft);
    },

  /** Cmd+Z in de avatar-tekenaar. Zie handleKey in logic/lifecycle.js. */
  avUndo() {
      const stapel = this.state.avUndo || [];
      if (!stapel.length) return false;
      const terug = stapel[stapel.length - 1];
      this.setState({
        avUndo: stapel.slice(0, -1),
        avRedo: (this.state.avRedo || []).concat([this.state.editAvatar || AV_EMPTY]).slice(-40),
        editAvatar: terug,
        profileDirty: true
      }, this.keepAvatarDraft);
      return true;
    },

  avRedoStep() {
      const stapel = this.state.avRedo || [];
      if (!stapel.length) return false;
      const vooruit = stapel[stapel.length - 1];
      this.setState({
        avRedo: stapel.slice(0, -1),
        avUndo: (this.state.avUndo || []).concat([this.state.editAvatar || AV_EMPTY]).slice(-40),
        editAvatar: vooruit,
        profileDirty: true
      }, this.keepAvatarDraft);
      return true;
    },

  // Wie nog geen account heeft, mag alvast tekenen: dat avatartje blijft bewaard
  // en staat er de volgende keer weer, tot het account gemaakt is.
  keepAvatarDraft() {
      if (this.state.accountId) return;
      try {
        const av = this.state.editAvatar || '';
        if (/[0-9a-f]/.test(av)) store.set('gp_avatar_draft', av);
        else store.remove('gp_avatar_draft');
      } catch (e) {}
    }
};
